import React, { useState, useEffect, useRef } from "react";
import { Link } from 'react-router-dom';
import { useLeaguePrefix, buildTeamLink, buildPublicTeamLink, buildMatchLink } from "../../utils/leagueLinks";
import { useCurrentTeamId } from "../../hooks/useCurrentTeamId";
import "../../styles/match-card.css";
import ballIcon from '../../assets/messages/ball.png'; // Import ball icon
import { supabase } from '../../lib/supabaseClient'; // Import Supabase client

const FRONTEND_FALLBACK_LOGO_ASSET = ballIcon; // Path to the frontend asset
const BACKEND_DEFAULT_TEAM_LOGO_PATH = '/uploads/teams/7b9a405a6a953b17e04a8f0107c3e5fd.jpg'; // Canonical backend default

// Helper function to clean up and validate logo URLs
const getCleanLogoUrl = (logoUrl) => {
  if (!logoUrl || logoUrl.trim() === "" || logoUrl === "/uploads/teams/default-team-logo.png") {
    return BACKEND_DEFAULT_TEAM_LOGO_PATH;
  }
  
  // If it's already our fallback, return as-is
  if (logoUrl === FRONTEND_FALLBACK_LOGO_ASSET) {
    return logoUrl;
  }
  
  // Handle malformed URLs that start with /https:// or /http://
  if (logoUrl.startsWith('/https://') || logoUrl.startsWith('/http://')) {
    return logoUrl.substring(1); // Remove the leading slash
  }
  
  // Handle regular absolute URLs
  if (logoUrl.startsWith('https://') || logoUrl.startsWith('http://')) {
    return logoUrl;
  }
  
  // Handle valid relative paths from backend (these should be served by the backend proxy)
  if (logoUrl.startsWith('/uploads/')) {
    return logoUrl;
  }
  
  console.warn(`[getCleanLogoUrl] Unrecognized logo URL format: "${logoUrl}". Falling back to backend default.`);
  return BACKEND_DEFAULT_TEAM_LOGO_PATH;
};

// Helper function to extract file path from Supabase URL
const extractSupabaseFilePath = (url) => {
  if (!url || !url.includes('supabase.co')) return null;
  
  // Extract path from URLs like: https://xsrwligqdlmkyqdsczru.supabase.co/storage/v1/object/public/team-logos/67e9571ff02fc4024095e886/logo.jpg
  const match = url.match(/\/storage\/v1\/object\/public\/(.*)/);
  return match ? match[1] : null;
};

// Component for handling individual team logo with error tracking and authenticated loading
const TeamLogo = ({ logoUrl, teamName, className = "" }) => {
  const [currentSrc, setCurrentSrc] = useState(FRONTEND_FALLBACK_LOGO_ASSET);
  const [hasErrored, setHasErrored] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const errorCountRef = useRef(0);
  const blobUrlRef = useRef(null);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => { // Check against FRONTEND_FALLBACK_LOGO_ASSET
      if (blobUrlRef.current && blobUrlRef.current !== FRONTEND_FALLBACK_LOGO_ASSET && blobUrlRef.current.startsWith('blob:')) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
    };
  }, []);

  // Load image when logoUrl changes
  useEffect(() => {
    setHasErrored(false);
    errorCountRef.current = 0;
    setIsLoading(false);

    const loadImage = async () => {
      if (!logoUrl) {
        setCurrentSrc(FRONTEND_FALLBACK_LOGO_ASSET);
        return;
      }

      const cleanUrl = getCleanLogoUrl(logoUrl);
      
      // If it's not a Supabase URL, use it directly
      if (!cleanUrl.includes('supabase.co')) {
        setCurrentSrc(cleanUrl);
        return;
      }

      // For Supabase URLs, use authenticated download
      const filePath = extractSupabaseFilePath(cleanUrl);
      if (!filePath) {
        console.warn(`Could not extract file path from Supabase URL: ${cleanUrl}`);
        setCurrentSrc(FRONTEND_FALLBACK_LOGO_ASSET);
        return;
      }

      setIsLoading(true);
      
      try {
        // Determine bucket from file path
        const [bucketName, ...pathParts] = filePath.split('/');
        const fileName = pathParts.join('/');
        
        const { data, error } = await supabase.storage
          .from(bucketName)
          .download(fileName);
        
        if (error) {
          throw error;
        }
        
        // Cleanup previous blob URL
        if (blobUrlRef.current && blobUrlRef.current !== FRONTEND_FALLBACK_LOGO_ASSET && blobUrlRef.current.startsWith('blob:')) {
          URL.revokeObjectURL(blobUrlRef.current);
        }
        
        // Create new blob URL
        const blobUrl = URL.createObjectURL(data);
        blobUrlRef.current = blobUrl;
        setCurrentSrc(blobUrl);
        setIsLoading(false);
        
      } catch (error) {
        console.log(`Error loading ${teamName} logo from Supabase:`, error.message);
        setCurrentSrc(FRONTEND_FALLBACK_LOGO_ASSET);
        setHasErrored(true);
        setIsLoading(false);
      }
    };

    loadImage();
  }, [logoUrl, teamName]);

  const handleError = (e) => {
    errorCountRef.current += 1;
    
    // Prevent infinite loops - max 1 error attempt for authenticated loading
    if (errorCountRef.current > 1 || hasErrored) {
      console.warn(`Too many errors for ${teamName} logo, stopping retries`);
      return;
    }

    console.log(`Error loading ${teamName} logo (attempt ${errorCountRef.current}):`, e.target.src);
    
    // Set fallback and mark as errored
    setHasErrored(true);
    setCurrentSrc(FRONTEND_FALLBACK_LOGO_ASSET);
  };

  // Show loading state for Supabase images
  if (isLoading) {
    return (
      <img 
        src={FRONTEND_FALLBACK_LOGO_ASSET} 
        alt={`Loading ${teamName || 'team'} logo...`} 
        className={`${className} loading-opacity`}
        style={{ opacity: 0.5 }}
      />
    );
  }

  return (
    <img 
      src={currentSrc} 
      alt={teamName || 'Team logo'} 
      className={className}
      onError={handleError}
    />
  );
};

/**
 * Component to display a single match
 * @param {Object} props - Component props
 * @param {Object} props.match - Match data
 * @returns {JSX.Element} - Match card component
 */
export const MatchCard = ({ match }) => {
    const prefix = useLeaguePrefix();
    const currentTeamId = useCurrentTeamId();
  // Format match date
  const formatMatchDate = (dateString) => {
    if (!dateString) return "TBD";
    
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      hour12: false,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };
  
  // Get status class
  const getStatusClass = (status) => {
    switch (status) {
      case 'completed': return 'status-completed';
      case 'in_progress': return 'status-in-progress';
      case 'cancelled': return 'status-cancelled';
      default: return 'status-scheduled';
    }
  };
  
  // Get status label
  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed': return 'Completed';
      case 'in_progress': return 'Live';
      case 'cancelled': return 'Cancelled';
      default: return 'Scheduled';
    }
  };

  // Clean team logo URLs
  const homeLogoUrl = getCleanLogoUrl(match?.homeTeam?.logo);
  const awayLogoUrl = getCleanLogoUrl(match?.awayTeam?.logo);
  
  return (
    <div className={`match-card ${getStatusClass(match.status)}`}>
      <div className="match-time">
        <span className="date">{formatMatchDate(match.date)}</span>
        <span className={`status ${getStatusClass(match.status)}`}>
          {getStatusLabel(match.status)}
        </span>
      </div>
      
      <div className="teams-container">
        <div className="team home-team">
          <div className="team-logo">
            <TeamLogo 
              logoUrl={homeLogoUrl} 
              teamName={match?.homeTeam?.name}
            />
          </div>
          <div className="team-name">
            <Link to={match?.homeTeam?._id === currentTeamId ? buildTeamLink(match?.homeTeam?._id, prefix) : buildPublicTeamLink(match?.homeTeam?._id, prefix)}>{match?.homeTeam?.name || 'Unknown Team'}</Link>
          </div>
        </div>
        
        <div className="match-score">
          {match.status === 'completed' || match.status === 'in_progress' ? (
            <span className="score">{match.homeScore} - {match.awayScore}</span>
          ) : (
            <span className="vs">VS</span>
          )}
        </div>
        
        <div className="team away-team">
          <div className="team-logo">
            <TeamLogo 
              logoUrl={awayLogoUrl} 
              teamName={match?.awayTeam?.name}
            />
          </div>
          <div className="team-name">
            <Link to={match?.awayTeam?._id === currentTeamId ? buildTeamLink(match?.awayTeam?._id, prefix) : buildPublicTeamLink(match?.awayTeam?._id, prefix)}>{match?.awayTeam?.name || 'Unknown Team'}</Link>
          </div>
        </div>
      </div>
      
      <div className="match-actions">
        <Link to={buildMatchLink(match._id, 'match', prefix)} className="match-details-btn">
          Match Details
        </Link>
      </div>
    </div>
  );
};
